import React, { useState, useRef, useEffect } from 'react';

const LichessStudyCarousel = ({
  studies = [],
  className = '',
  showDots = true,
  showArrows = true,
  accentColor = '#749555',
  layout = 'linkblock',
  boardTheme = 'brown'
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [startX, setStartX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const carouselRef = useRef(null);

  const goToSlide = (index) => {
    if (isTransitioning || studies.length === 0) return;

    setIsTransitioning(true);
    setCurrentIndex(index);

    setTimeout(() => setIsTransitioning(false), layout === 'tabs' ? 250 : 300);
  };

  const nextSlide = () => {
    // Circular navigation for all layouts
    const nextIndex = (currentIndex + 1) % studies.length;
    goToSlide(nextIndex);
  };

  const prevSlide = () => {
    // Circular navigation for all layouts
    const prevIndex = currentIndex === 0 ? studies.length - 1 : currentIndex - 1;
    goToSlide(prevIndex);
  };

  // Touch/Mouse drag handlers
  const handleStart = (clientX) => {
    if (studies.length <= 1) return;
    setIsDragging(true);
    setStartX(clientX);
    setDragOffset(0);
  };

  const handleMove = (clientX) => {
    if (!isDragging || studies.length <= 1) return;

    const deltaX = clientX - startX;
    setDragOffset(deltaX);
  };

  const handleEnd = () => {
    if (!isDragging || studies.length <= 1) return;

    setIsDragging(false);

    // Determine if we should switch slides based on drag distance
    const threshold = 50;
    const carouselWidth = carouselRef.current?.offsetWidth || 300;
    const dragPercentage = Math.abs(dragOffset) / carouselWidth;

    if (layout === 'tabs') {
      // Tab layout: simple threshold
      if (Math.abs(dragOffset) > threshold) {
        if (dragOffset > 0) {
          prevSlide();
        } else {
          nextSlide();
        }
      }
    } else {
      // Link block layout: threshold + percentage check
      if (Math.abs(dragOffset) > threshold && dragPercentage > 0.2) {
        if (dragOffset > 0) {
          prevSlide();
        } else {
          nextSlide();
        }
      }
    }

    setDragOffset(0);
  };

  // Mouse events
  const handleMouseDown = (e) => {
    e.preventDefault();
    handleStart(e.clientX);
  };

  const handleMouseMove = (e) => {
    handleMove(e.clientX);
  };

  const handleMouseUp = () => {
    handleEnd();
  };

  // Touch events
  const handleTouchStart = (e) => {
    handleStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    handleMove(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    handleEnd();
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (studies.length <= 1) return;

      if (e.key === 'ArrowLeft') {
        prevSlide();
      } else if (e.key === 'ArrowRight') {
        nextSlide();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, studies.length]);

  // Add global mouse move/up listeners when dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, startX]);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (!studies || studies.length === 0) {
    const bgColor = layout === 'tabs' ? 'bg-gray-800' : 'bg-gray-100';
    const textColor = layout === 'tabs' ? 'text-gray-400' : 'text-gray-500';
    return (
      <div className={`${bgColor} rounded-lg flex items-center justify-center h-64 ${className}`}>
        <div className="text-center">
          <i className="fas fa-chess-board text-4xl text-gray-800 mb-4"></i>
          <p className={textColor}>No studies to display</p>
        </div>
      </div>
    );
  }

  // Generate iframe src for current study with global theme (using theme parameter for Lichess)
  const generateIframeSrc = (study) => {
    const theme = boardTheme || 'brown';
    let baseUrl;
    if (study.chapterId) {
      baseUrl = `https://lichess.org/study/embed/${study.studyId}/${study.chapterId}`;
    } else {
      baseUrl = `https://lichess.org/study/embed/${study.studyId}`;
    }
    return `${baseUrl}?theme=${theme}&bg=dark`;
  };

  // Tab Layout: Modern Peek Carousel
  if (layout === 'tabs') {
    const hasPrev = studies.length >= 3; // Left peek (prev) - show only if 3+ items
    const hasNext = studies.length >= 2; // Right peek (next) - show if 2+ items

    // Mobile: Single centered study only
    if (isMobile) {
      return (
        <div className={`relative w-full ${className}`} ref={carouselRef}>
          <div className="flex justify-center">
            <div
              className="w-full max-w-md"
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className={`relative w-full rounded-xl overflow-hidden shadow-2xl transition-opacity duration-250 ${isTransitioning ? 'opacity-50' : 'opacity-100'}`} style={{ aspectRatio: '1 / 1.15' }}>
                <div style={{ 
                  transform: 'scale(1.02)', 
                  transformOrigin: 'top left', 
                  width: '98%',
                  height: '100%'
                }}>
                <iframe
                    key={`${studies[currentIndex]?.id || currentIndex}-${boardTheme}`}
                  src={generateIframeSrc(studies[currentIndex])}
                  title={studies[currentIndex].title || `Study ${currentIndex + 1}`}
                  frameBorder="0"
                    width="600"
                    height="371"
                    style={{ 
                      pointerEvents: isDragging ? 'none' : 'auto',
                      maxWidth: 'none',
                      display: 'block'
                    }}
                ></iframe>
                </div>
              </div>

              {/* Study Info */}
              {studies[currentIndex].title && (
                <div className="mt-4 text-center">
                  <h3 className="text-xl font-bold text-white mb-1">{studies[currentIndex].title}</h3>
                </div>
              )}

              {/* Counter */}
              {studies.length > 1 && (
                <div className="mt-2 text-center text-sm text-gray-500 flex items-center justify-center gap-2">
                  <i
                    className="fas fa-chevron-left text-xs cursor-pointer hover:text-gray-300 transition-colors"
                    onClick={prevSlide}
                  ></i>
                  <span>{currentIndex + 1} / {studies.length}</span>
                  <i
                    className="fas fa-chevron-right text-xs cursor-pointer hover:text-gray-300 transition-colors"
                    onClick={nextSlide}
                  ></i>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Desktop: Peek preview layout with wrap-around
    const nextIndex = (currentIndex + 1) % studies.length;
    const prevIndex = (currentIndex - 1 + studies.length) % studies.length;

    return (
      <div className={`relative w-full overflow-visible ${className}`} ref={carouselRef}>
        {/* Modern Peek Carousel */}
        <div className="relative flex items-center justify-center gap-4">
          {/* Previous Peek */}
          {hasPrev && (
            <div
              className="block w-1/4 flex-shrink-0 cursor-pointer transition-all duration-300 hover:scale-105"
              onClick={isTransitioning ? undefined : prevSlide}
            >
              <div className="relative w-full rounded-lg overflow-hidden opacity-50 hover:opacity-75" style={{ aspectRatio: '1 / 1' }}>
                <div style={{ 
                  transform: 'scale(0.68)', 
                  transformOrigin: 'top left', 
                  width: '147.1%',
                  height: '100%'
                }}>
                <iframe
                  src={generateIframeSrc(studies[prevIndex])}
                  title={studies[prevIndex].title || `Study ${prevIndex + 1}`}
                  frameBorder="0"
                    width="600"
                    height="371"
                    style={{ 
                      pointerEvents: 'none',
                      maxWidth: 'none',
                      display: 'block'
                    }}
                ></iframe>
                </div>
                <div className="absolute inset-0 bg-black/40"></div>
              </div>
            </div>
          )}

          {/* Main Study */}
          <div
            className="w-2/5 flex-shrink-0"
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className={`relative w-full rounded-xl overflow-hidden shadow-2xl transition-opacity duration-250 ${isTransitioning ? 'opacity-50' : 'opacity-100'}`} style={{ aspectRatio: '1 / 1.15' }}>
              <div style={{ 
                transform: 'scale(1.08)', 
                transformOrigin: 'top left', 
                width: '92.6%',
                height: '100%'
              }}>
              <iframe
                  key={`${studies[currentIndex]?.id || currentIndex}-${boardTheme}`}
                src={generateIframeSrc(studies[currentIndex])}
                title={studies[currentIndex].title || `Study ${currentIndex + 1}`}
                frameBorder="0"
                  width="600"
                  height="371"
                  style={{ 
                    pointerEvents: isDragging ? 'none' : 'auto',
                    maxWidth: 'none',
                    display: 'block'
                  }}
              ></iframe>
              </div>
            </div>

            {/* Study Info */}
            {studies[currentIndex].title && (
              <div className="mt-4 text-center">
                <h3 className="text-xl font-bold text-white mb-1">{studies[currentIndex].title}</h3>
              </div>
            )}

            {/* Counter */}
            {studies.length > 1 && (
              <div className="mt-2 text-center text-sm text-gray-500 flex items-center justify-center gap-2">
                <i
                  className="fas fa-chevron-left text-xs cursor-pointer hover:text-gray-300 transition-colors"
                  onClick={prevSlide}
                ></i>
                <span>{currentIndex + 1} / {studies.length}</span>
                <i
                  className="fas fa-chevron-right text-xs cursor-pointer hover:text-gray-300 transition-colors"
                  onClick={nextSlide}
                ></i>
              </div>
            )}
          </div>

          {/* Next Peek */}
          {hasNext && (
            <div
              className="block w-1/4 flex-shrink-0 cursor-pointer transition-all duration-300 hover:scale-105"
              onClick={isTransitioning ? undefined : nextSlide}
            >
              <div className="relative w-full rounded-lg overflow-hidden opacity-50 hover:opacity-75" style={{ aspectRatio: '1 / 1' }}>
                <div style={{ 
                  transform: 'scale(0.68)', 
                  transformOrigin: 'top left', 
                  width: '147.1%',
                  height: '100%'
                }}>
                <iframe
                  src={generateIframeSrc(studies[nextIndex])}
                  title={studies[nextIndex].title || `Study ${nextIndex + 1}`}
                  frameBorder="0"
                    width="600"
                    height="371"
                    style={{ 
                      pointerEvents: 'none',
                      maxWidth: 'none',
                      display: 'block'
                    }}
                ></iframe>
                </div>
                <div className="absolute inset-0 bg-black/40"></div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Link Block Layout: Use same layout as Tab Layout
  const hasPrev = studies.length >= 3; // Left peek (prev) - show only if 3+ items
  const hasNext = studies.length >= 2; // Right peek (next) - show if 2+ items

  // Mobile: Single centered study only
  if (isMobile) {
    return (
      <div className={`relative w-full ${className}`} ref={carouselRef}>
        <div className="flex justify-center">
          <div
            className="w-full max-w-md"
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className={`relative w-full rounded-xl overflow-hidden shadow-2xl transition-opacity duration-250 ${isTransitioning ? 'opacity-50' : 'opacity-100'}`} style={{ aspectRatio: '1 / 1.15' }}>
              <div style={{ 
                transform: 'scale(1.1)', 
                transformOrigin: 'top left', 
                width: '90.9%',
                height: '100%'
              }}>
              <iframe
                  key={`${studies[currentIndex]?.id || currentIndex}-${boardTheme}`}
                src={generateIframeSrc(studies[currentIndex])}
                title={studies[currentIndex].title || `Study ${currentIndex + 1}`}
                frameBorder="0"
                  width="600"
                  height="371"
                  style={{ 
                    pointerEvents: isDragging ? 'none' : 'auto',
                    maxWidth: 'none',
                    display: 'block'
                  }}
              ></iframe>
              </div>
            </div>

            {/* Study Info */}
            {studies[currentIndex].title && (
              <div className="mt-4 text-center">
                <h3 className="text-xl font-bold text-white mb-1">{studies[currentIndex].title}</h3>
              </div>
            )}

            {/* Counter */}
            {studies.length > 1 && (
              <div className="mt-2 text-center text-sm text-gray-500 flex items-center justify-center gap-2">
                <i
                  className="fas fa-chevron-left text-xs cursor-pointer hover:text-gray-300 transition-colors"
                  onClick={prevSlide}
                ></i>
                <span>{currentIndex + 1} / {studies.length}</span>
                <i
                  className="fas fa-chevron-right text-xs cursor-pointer hover:text-gray-300 transition-colors"
                  onClick={nextSlide}
                ></i>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Desktop: Peek preview layout with wrap-around
  const nextIndex = (currentIndex + 1) % studies.length;
  const prevIndex = (currentIndex - 1 + studies.length) % studies.length;

  return (
    <div className={`relative w-full overflow-visible ${className}`} ref={carouselRef}>
      {/* Modern Peek Carousel */}
      <div className="relative flex items-center justify-center gap-4">
        {/* Previous Peek */}
        {hasPrev && (
          <div
            className="block w-1/4 flex-shrink-0 cursor-pointer transition-all duration-300 hover:scale-105"
            onClick={isTransitioning ? undefined : prevSlide}
          >
            <div className="relative w-full rounded-lg overflow-hidden opacity-50 hover:opacity-75" style={{ aspectRatio: '1 / 1' }}>
              <div style={{ 
                transform: 'scale(0.68)', 
                transformOrigin: 'top left', 
                width: '147.1%',
                height: '100%'
              }}>
              <iframe
                src={generateIframeSrc(studies[prevIndex])}
                title={studies[prevIndex].title || `Study ${prevIndex + 1}`}
                frameBorder="0"
                  width="600"
                  height="371"
                  style={{ 
                    pointerEvents: 'none',
                    maxWidth: 'none',
                    display: 'block'
                  }}
              ></iframe>
              </div>
              <div className="absolute inset-0 bg-black/40"></div>
            </div>
          </div>
        )}

        {/* Main Study */}
        <div
          className="w-2/5 flex-shrink-0"
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className={`relative w-full rounded-xl overflow-hidden shadow-2xl transition-opacity duration-250 ${isTransitioning ? 'opacity-50' : 'opacity-100'}`} style={{ aspectRatio: '1 / 1.15' }}>
            <div style={{ 
              transform: 'scale(1.08)', 
              transformOrigin: 'top left', 
              width: '92.6%',
              height: '100%'
            }}>
            <iframe
                key={`${studies[currentIndex]?.id || currentIndex}-${boardTheme}`}
              src={generateIframeSrc(studies[currentIndex])}
              title={studies[currentIndex].title || `Study ${currentIndex + 1}`}
              frameBorder="0"
                width="600"
                height="371"
                style={{ 
                  pointerEvents: isDragging ? 'none' : 'auto',
                  maxWidth: 'none',
                  display: 'block'
                }}
            ></iframe>
            </div>
          </div>

          {/* Study Info */}
          {studies[currentIndex].title && (
            <div className="mt-4 text-center">
              <h3 className="text-xl font-bold text-white mb-1">{studies[currentIndex].title}</h3>
            </div>
          )}

          {/* Counter */}
          {studies.length > 1 && (
            <div className="mt-2 text-center text-sm text-gray-500 flex items-center justify-center gap-2">
              <i
                className="fas fa-chevron-left text-xs cursor-pointer hover:text-gray-300 transition-colors"
                onClick={prevSlide}
              ></i>
              <span>{currentIndex + 1} / {studies.length}</span>
              <i
                className="fas fa-chevron-right text-xs cursor-pointer hover:text-gray-300 transition-colors"
                onClick={nextSlide}
              ></i>
            </div>
          )}
        </div>

        {/* Next Peek */}
        {hasNext && (
          <div
            className="block w-1/4 flex-shrink-0 cursor-pointer transition-all duration-300 hover:scale-105"
            onClick={isTransitioning ? undefined : nextSlide}
          >
            <div className="relative w-full rounded-lg overflow-hidden opacity-50 hover:opacity-75" style={{ aspectRatio: '1 / 1' }}>
              <div style={{ 
                transform: 'scale(0.68)', 
                transformOrigin: 'top left', 
                width: '147.1%',
                height: '100%'
              }}>
              <iframe
                src={generateIframeSrc(studies[nextIndex])}
                title={studies[nextIndex].title || `Study ${nextIndex + 1}`}
                frameBorder="0"
                  width="600"
                  height="371"
                  style={{ 
                    pointerEvents: 'none',
                    maxWidth: 'none',
                    display: 'block'
                  }}
              ></iframe>
              </div>
              <div className="absolute inset-0 bg-black/40"></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LichessStudyCarousel;
